import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const ScrollToTop = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const retryRef = useRef(null);
  
  // Shield scroll saving during route transitions/restorations
  const isRestoringRef = useRef(false);
  
  // Use a ref to hold the current location so we only bind the scroll event once
  const locationRef = useRef(location);
  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  // We can use session storage or a memory cache
  const getCacheKey = (loc) => {
    return loc.key && loc.key !== 'default' ? loc.key : (loc.pathname + loc.search);
  };

  // Save scroll position on scroll
  useEffect(() => {
    const handleScroll = () => {
      // If we are in the middle of restoring or transitioning, do not save
      // to avoid corrupting the scroll coordinates with intermediate values.
      if (isRestoringRef.current) return;
      
      const currentKey = getCacheKey(locationRef.current);
      const position = window.scrollY;
      const height = document.documentElement.scrollHeight;
      
      // Save in session storage
      try {
        sessionStorage.setItem(`scroll_pos_${currentKey}`, position.toString());
        sessionStorage.setItem(`scroll_height_${currentKey}`, height.toString());
      } catch (e) {
        // Ignore storage errors
      }
    };

    // Shield synchronously on clicking local links BEFORE unmount happens
    const handleLinkClick = (e) => {
      const link = e.target.closest('a');
      const isLocal = link && link.href && link.host === window.location.host;
      const isSpecial = link && (link.target || link.href.startsWith('tel:') || link.href.startsWith('mailto:') || link.href.startsWith('javascript:'));
      
      if (isLocal && !isSpecial) {
        isRestoringRef.current = true;
      }
    };

    // Shield synchronously on browser back/forward buttons
    const handlePopState = () => {
      isRestoringRef.current = true;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('click', handleLinkClick, { capture: true, passive: true });
    window.addEventListener('popstate', handlePopState, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('click', handleLinkClick, { capture: true });
      window.removeEventListener('popstate', handlePopState);
    };
  }, []); // Bind only once on mount

  // Handle route change
  useEffect(() => {
    // Shield scroll events starting now
    isRestoringRef.current = true;

    // Clear any existing restoration retries
    const stopRestoration = () => {
      if (retryRef.current) {
        clearInterval(retryRef.current);
        retryRef.current = null;
      }
    };

    stopRestoration();

    const currentKey = getCacheKey(location);
    let targetScroll = 0;
    let targetHeight = 0;

    if (navigationType === 'POP') {
      try {
        const savedPos = sessionStorage.getItem(`scroll_pos_${currentKey}`);
        const savedHeight = sessionStorage.getItem(`scroll_height_${currentKey}`);
        if (savedPos !== null) {
          targetScroll = parseInt(savedPos, 10);
        }
        if (savedHeight !== null) {
          targetHeight = parseInt(savedHeight, 10);
        }
      } catch (e) {
        // Ignore
      }
    }

    // Temporarily expand document height to allow instant jump on the first frame
    if (targetScroll > 0 && targetHeight > 0) {
      document.documentElement.style.minHeight = targetHeight + 'px';
      document.body.style.minHeight = targetHeight + 'px';
      document.documentElement.style.scrollBehavior = 'auto'; // Prevent any smooth scroll animations
    }

    // Scroll to the target instantly
    window.scrollTo(0, targetScroll);

    function finishRestoration() {
      stopRestoration();
      cleanupListeners();
      // Keep shielding active briefly to let any leftover browser scroll events settle
      setTimeout(() => {
        isRestoringRef.current = false;
      }, 150);
    }

    function cancelOnInteraction() {
      finishRestoration();
    }

    function cleanupListeners() {
      window.removeEventListener('wheel', cancelOnInteraction);
      window.removeEventListener('touchmove', cancelOnInteraction);
      window.removeEventListener('mousedown', cancelOnInteraction);
      window.removeEventListener('keydown', cancelOnInteraction);
      
      // Restore styling back to natural
      document.documentElement.style.minHeight = '';
      document.body.style.minHeight = '';
      document.documentElement.style.scrollBehavior = '';
    }

    // If the target is not 0, the page might not have fully loaded or rendered its dynamic contents.
    // We will attempt to restore the scroll position over a short period.
    if (targetScroll > 0) {
      let attempts = 0;
      const maxAttempts = 30; // 30 frames/checks (approx 1.5 seconds)

      window.addEventListener('wheel', cancelOnInteraction, { passive: true });
      window.addEventListener('touchmove', cancelOnInteraction, { passive: true });
      window.addEventListener('mousedown', cancelOnInteraction, { passive: true });
      window.addEventListener('keydown', cancelOnInteraction, { passive: true });

      // Let's check periodically if the scroll height is enough to satisfy the target scroll
      retryRef.current = setInterval(() => {
        attempts++;
        
        // Try scrolling to target
        window.scrollTo(0, targetScroll);

        // Keep scrolling to target scroll. We only finish automatically after maxAttempts (1.5 seconds).
        // Any manual user interaction (wheel, touch, click, key) will cancel immediately.
        if (attempts > maxAttempts) {
          finishRestoration();
        }
      }, 50);

      return () => {
        finishRestoration();
      };
    } else {
      // For PUSH/REPLACE where target is 0, settle scroll events and unshield
      const timer = setTimeout(() => {
        isRestoringRef.current = false;
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [location, navigationType]);

  return null;
};

export default ScrollToTop;

